import React, { useState } from 'react';
import { NextPage } from 'next';
import useProducts from '../../hooks/use-products';
import DropdownField from '../../components/dropdown-field';
import {
  modelOptions,
  ownerCategoryOptions,
  ownerTypeOptions,
  yearOptions,
} from '../../lib/bookstore-util';
import OwnersLiteratureLayout from '../../components/@bookstore/bookstore-owner-layout';
import BookstoreProductGrid from '../../components/@bookstore/bookstore-product-grid';
import InputField from '../../components/input-field';
import Button from '../../components/button';

const OwnersLiteraturePage: NextPage = () => {
  const [pageIndex, setPageIndex] = useState(1);
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [search, setSearch] = useState('');
  const [filterConditions, setFilterConditions] = useState([
    { code: 'status', namespace: '', value: 'ACTIVE' },
    { code: 'itemclass', namespace: '', value: 'OWNER' },
    { code: 'itemtype', namespace: '', value: 'Glove Compartment Items' },
    { code: 'itemtype', namespace: '', value: 'Miscellaneous' },
    { code: 'purchaseoption', namespace: '', value: 'consumer' },
    { code: 'purchaseoption', namespace: '', value: 'internal_access' },
    { code: 'purchaseoption', namespace: '', value: 'off' },
  ]);

  const { data, loading, error } = useProducts({
    conditions: filterConditions,
    page: pageIndex - 1,
    size: 12,
    sort: 'data.created.dsc',
  });

  if (typeof window === 'undefined') {
    return null;
  }

  const onCategoryChange = (e) => {
    setCategory(e.target.value);

    const temp = filterConditions.filter((fc) => fc.code !== 'itemtype');

    if (e.target.value !== '') {
      temp.push({ code: 'itemtype', namespace: '', value: e.target.value });
    }

    setFilterConditions(temp);
    setPageIndex(1);
  };

  const onTypeChange = (e) => {
    setType(e.target.value);

    const temp = filterConditions.filter((fc) => fc.code !== 'itemsubtype');

    if (e.target.value !== '') {
      temp.push({ code: 'itemsubtype', namespace: '', value: e.target.value });
    }

    setFilterConditions(temp);
    setPageIndex(1);
  };

  const onModelChange = (e) => {
    setModel(e.target.value);

    const temp =
      year === ''
        ? filterConditions.filter(
            (fc) => fc.code !== 'model' && fc.code !== 'year'
          )
        : filterConditions.filter((fc) => fc.code !== 'model');

    if (e.target.value !== '') {
      temp.push({
        code: 'model',
        namespace: '',
        value: e.target.value,
      });
    }

    if (year === '') {
      yearOptions.forEach((y) =>
        temp.push({
          code: 'year',
          namespace: '',
          value: y.value,
        })
      );
    }

    setFilterConditions(temp);
    setPageIndex(1);
  };

  const onYearChange = (e) => {
    setYear(e.target.value);

    const temp =
      model === ''
        ? filterConditions.filter(
            (fc) => fc.code !== 'year' && fc.code !== 'model'
          )
        : filterConditions.filter((fc) => fc.code !== 'year');

    if (e.target.value !== '') {
      temp.push({
        code: 'year',
        namespace: '',
        value: e.target.value,
      });
    }

    if (model === '') {
      modelOptions.forEach((m) =>
        temp.push({
          code: 'model',
          namespace: '',
          value: m.value,
        })
      );
    }

    setFilterConditions(temp);
    setPageIndex(1);
  };

  const onSearchChange = () => {
    const temp = filterConditions.filter((fc) => fc.code !== '_search');

    if (search !== '') {
      temp.push({ code: '_search', namespace: '', value: search });
    }

    setFilterConditions(temp);
    setPageIndex(1);
  };

  const component = (children) => (
    <OwnersLiteratureLayout>
      <div className="search">
        <div className="group">
          <InputField
            id="search"
            label="Search"
            type="text"
            defaultValue={search}
            onChange={(e) => setSearch(e.target.value)}
            marginBottom="0"
          />
          <Button text="Search" onClick={() => onSearchChange()} />
        </div>
        <DropdownField
          id="model"
          label="Model"
          options={modelOptions}
          value={model}
          onChange={onModelChange}
        />
        <DropdownField
          id="year"
          label="Year"
          options={yearOptions}
          value={year}
          onChange={onYearChange}
        />
        <DropdownField
          id="category"
          label="Category"
          options={ownerCategoryOptions}
          value={category}
          onChange={onCategoryChange}
        />
        <DropdownField
          id="type"
          label="Type"
          options={ownerTypeOptions}
          value={type}
          onChange={onTypeChange}
        />
      </div>
      {children}
      {/* language=CSS */}
      <style jsx>{`
        .search {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          grid-gap: 1rem;
        }

        @media screen and (max-width: 850px) {
          .search {
            grid-template-columns: 1fr;
            padding: 0 2rem;
          }
        }

        .search input[type='text'] {
          appearance: none;
          padding: 0;
          height: 48px;
          font-family: 'Volvo Novum', sans-serif;
          font-size: 1rem;
          font-weight: 400;
          background: none;
          border: none;
          border-radius: 0;
          border-bottom: 1px solid #707070;
          width: 100%;
        }

        .search input[type='text']:focus {
          outline: none;
        }

        :global(.product-search-filter) {
          margin-top: 2rem;
          font-family: 'Volvo Novum', sans-serif;
          font-weight: 400;
          font-size: 14px;
          padding: 0 2rem;
        }

        .group {
          display: flex;
          flex-direction: row;
          gap: 1rem;
          margin-bottom: 1rem;
        }
      `}</style>
    </OwnersLiteratureLayout>
  );

  if (loading || error) {
    return component(
      <p className="product-search-filter">
        Searching for products, please wait a minute.
      </p>
    );
  } else if (!data || data.products.content.length === 0) {
    return component(
      <p className="product-search-filter">
        Your search did not return any matching results. Please try a different
        search.
      </p>
    );
  } else {
    return component(
      <BookstoreProductGrid
        products={data.products}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
      />
    );
  }
};

export default OwnersLiteraturePage;
